import { rem } from 'polished';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import styled from 'styled-components';

import { BigPictureClient, Event } from '@vimeo/bigpicture-client';
import { translate } from '@vimeo/i18n-runtime';
import { blue, slate } from '@vimeo/iris/color';
import { Input, Button } from '@vimeo/iris/components';
import { Lock, CircleInfoSmall } from '@vimeo/iris/icons';
import { Paragraph, Text, Link, Header } from '@vimeo/iris/typography';

interface Props {
  email?: string;
  onEmailChange: (email: string) => void;
  handleSSOLogin: () => void;
  disableEmailUpdate?: boolean;
  onSSOFlowChoice: (activateSSOFlow: boolean) => void;
  checkInProgress: boolean;
  isSSO: boolean;
  firstTimeSSOLogin: boolean;
  customDomain?: string;
  switchType?: MouseEventHandler;
  disclaimer?: string;
  termsAndConditions?: string;
}

const SSO_UPGRADE_LINK = 'https://vimeo.com/enterprise/contact-us?mkc=ent_sso_privacy';

const SSOUpgradeText = translate({
  singular:
    'SSO is not enabled for this domain. Interested in additional security for your team? {A}Contact us.{/A}',
  replacements: {
    A: (text: string) => (
      <a href={SSO_UPGRADE_LINK} target="_blank" rel="noreferrer">
        {text}
      </a>
    ),
  },
});

const IndicatorBlock = styled.div`
  margin-top: ${rem(12)};
  display: flex;
  justify-content: center;

  svg {
    width: ${rem(12)};
    height ${rem(12)};
    margin: ${rem(2)} ${rem(4)};
  }
`;

const ContinueButton = styled(Button)`
  width: 100%;
`;

const LinkWrapper = styled(Header)`
  text-align: center;
  font-weight: normal;
  padding-top: ${rem(16)};
  a {
    color: ${slate(800)} !important;
  }
`;

const Info = styled(CircleInfoSmall)`
  color: #00adef;
  width: ${rem(40)};
  height: ${rem(40)};
  margin: ${rem(4.8)} ${rem(9.6)} ${rem(4.8)} ${rem(4.8)};
  * {
    fill: currentColor;
  }
`;

const Upsell = styled.div`
  background: ${blue(68)};
  justify-content: center;
  padding: ${rem(10)} ${rem(16)} ${rem(10)} ${rem(10)};
  align-items: center;
  flex-direction: row;
  border-radius: ${rem(3)};
  display: flex;
  margin: ${rem(20)} 0;

  .sso-enterprise-upsell-text-container {
    text-align: left;
    line-height: ${rem(20)};
  }

  a {
    color: ${blue(600)};
    text-decoration: underline;
  }
`;

const FirstTimeSSONotice = styled.div`
  color: ${slate(800)} !important;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 173, 239, 0.15);
  border-radius: ${rem(3)};
  padding: ${rem(8)};
  margin-bottom: ${rem(14)};
  font-size: ${rem(13)};
  line-height: ${rem(22)};
  letter-spacing: ${rem(0)};
`;

const InfoIcon = styled(Info)`
  width: ${rem(40)};
  margin-right: ${rem(8)};
`;

const EmailInput = styled(Input)`
  input {
    padding: ${rem(16)} ${rem(8)};
  }
`;

const JoinContainer = styled.div`
  padding-top: ${rem(10)};
  margin-top: ${rem(30)};
  text-align: center;
  border-top: 1px solid #d3d3d3;
`;

const FormSection = styled.section`
  padding: ${rem(10)} 0;
`;

export default function SSOForm({
  email,
  onEmailChange,
  handleSSOLogin,
  disableEmailUpdate,
  checkInProgress,
  onSSOFlowChoice,
  isSSO,
  firstTimeSSOLogin,
  customDomain,
  switchType,
  disclaimer,
  termsAndConditions,
}: Props) {
  if (!disableEmailUpdate) {
    disableEmailUpdate = false;
  }

  const onChangeEmailField = (changeEvent: React.ChangeEvent<HTMLInputElement>) =>
    onEmailChange(changeEvent.target.value);
  const onResetEmail = () => {
    onEmailChange('');
    onSSOFlowChoice(false);
  };
  const isUpsellBlockActive = !isSSO && !checkInProgress && email && email.length > 0;

  const onJoinClick = (clickEvent: React.MouseEvent<Element, MouseEvent>) => {
    onSSOFlowChoice(false);
    onEmailChange('');
    switchType && switchType(clickEvent);
  };

  useEffect(() => {
    BigPictureClient.sendEvent(
      new Event('vimeo.impression', 5, {
        page: 'lohp',
        location: 'login_modal',
        name: 'impression_signin_with_sso',
        feature: 'login',
        type: 'general',
      }),
    );
  }, []);

  let buttonText = customDomain
    ? translate('Log into custom domain')
    : translate('Log in with SSO');

  if (checkInProgress) {
    buttonText = translate('Checking...');
  }

  return (
    <div>
      <EmailInput
        defaultValue={email}
        status={'neutral'}
        onChange={onChangeEmailField}
        disabled={disableEmailUpdate}
      />
      {!isUpsellBlockActive && (
        <IndicatorBlock>
          <Lock />
          <Paragraph size={3}>{translate('Single sign-on enabled')}</Paragraph>
        </IndicatorBlock>
      )}
      {firstTimeSSOLogin && (
        <FirstTimeSSONotice>
          <InfoIcon />
          <Text>
            {translate(
              'Your organization now requires you to log in through single-sign-on (SSO).',
            )}
          </Text>
        </FirstTimeSSONotice>
      )}
      {isUpsellBlockActive && (
        <Upsell>
          <span className="sso-enterprise-upsell-icon-container">
            <Info />
          </span>
          <span className="sso-enterprise-upsell-text-container">{SSOUpgradeText}</span>
        </Upsell>
      )}
      {termsAndConditions && (
        <FormSection>
          <Paragraph format="soft" size="3">
            {termsAndConditions}
          </Paragraph>
          {disclaimer && <Paragraph size="3">{disclaimer}</Paragraph>}
        </FormSection>
      )}
      <ContinueButton
        disabled={checkInProgress || !isSSO}
        /*@ts-ignore*/
        format={'positive'}
        size={'lg'}
        onClick={handleSSOLogin}
      >
        {buttonText}
      </ContinueButton>
      {!disableEmailUpdate ? (
        <LinkWrapper size={'6'}>
          <Link onClick={onResetEmail} format="soft">
            {translate('Use a different email address')}
          </Link>
        </LinkWrapper>
      ) : (
        ''
      )}
      {switchType && (
        <JoinContainer>
          <Paragraph size="2" format="alternative">
            {translate("Don't have an account?")}
            &nbsp;
            <Link onClick={onJoinClick} element="span" variant="minimal">
              {translate('Join')}
            </Link>
          </Paragraph>
        </JoinContainer>
      )}
    </div>
  );
}
